import { Box, Checkbox, Input, Stack, Text, Tooltip } from '@chakra-ui/react';
import InputMask from 'react-input-mask';
import { cardExpiryMaskProps } from 'v2/lib/cardExpiry';
import type { useCheckoutForm } from '../useCheckoutForm';
import { Field } from './Section';

type Controller = ReturnType<typeof useCheckoutForm>;

/**
 * Saisie d'une nouvelle carte : numéro, expiration, cryptogramme,
 * enregistrement. Le nom du titulaire (BillingName) est saisi dans la section
 * « Adresse de facturation », juste en dessous. Règles de validation identiques au rendu historique
 * (AMEX 15 chiffres / CVV 4, sinon 16 / 3).
 */
export function CardFields({ c }: { c: Controller }) {
  const { register, errors, isAmex, creditCardNumber, isContract } = c;
  const amex = isAmex(creditCardNumber ?? '');

  return (
    <Stack spacing={4} mt={3} className="CheckoutV2CardFields">
      <Field label="Numéro de carte" error={errors?.CreditCardNumber?.message as string | undefined} isRequired>
        <Input
          className="cv2-input cv2-input--mono"
          variant="unstyled"
          {...register('CreditCardNumber', {
            required: 'Numéro de carte requis',
            validate: (value) => {
              const cleanedValue = value.replace(/\s/g, '');
              if (isAmex(cleanedValue)) {
                return cleanedValue.length === 15 || 'Numéro de carte AMEX invalide';
              }
              return cleanedValue.length === 16 || 'Numéro de carte invalide';
            },
          })}
          as={InputMask}
          mask={amex ? '9999 999999 99999' : '9999 9999 9999 9999'}
          maskChar={null}
          inputMode="numeric"
          autoComplete="cc-number"
          placeholder="1234 5678 9012 3456"
        />
      </Field>

      <Stack direction="row" spacing={3}>
        <Field label="Expiration" error={errors?.Exp?.message as string | undefined} isRequired flex={1}>
          <Input
            className="cv2-input cv2-input--mono"
            variant="unstyled"
            {...register('Exp', {
              required: "Date d'expiration requise",
              pattern: { value: /[0-1][0-9] \/ [0-9][0-9]/, message: "Date d'expiration invalide" },
            })}
            as={InputMask}
            mask="99 / 99"
            maskChar={null}
            {...cardExpiryMaskProps}
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder="MM / AA"
          />
        </Field>
        <Field label="Cryptogramme" error={errors?.Cryptogram?.message as string | undefined} isRequired flex={1}>
          <Input
            className="cv2-input cv2-input--mono"
            variant="unstyled"
            {...register('Cryptogram', {
              required: 'Cryptogramme requis',
              pattern: { value: amex ? /^[0-9]{4}$/ : /^[0-9]{3}$/, message: 'Cryptogramme invalide' },
            })}
            as={InputMask}
            mask={amex ? '9999' : '999'}
            maskChar={null}
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder={amex ? '1234' : '123'}
          />
        </Field>
      </Stack>

      {isContract ? (
        <Tooltip hasArrow placement="top" label="L'enregistrement de votre carte est obligatoire pour activer le prélèvement automatique mensuel de votre abonnement.">
          <Box display="inline-block">
            <Checkbox isChecked isDisabled size="md">
              Enregistrer ce moyen de paiement (requis pour l'abonnement)
            </Checkbox>
          </Box>
        </Tooltip>
      ) : (
        <Checkbox {...register('SaveInfo')} size="md">
          Enregistrer ce moyen de paiement pour la prochaine fois
        </Checkbox>
      )}

      <Text className="cv2-small">Vos données bancaires sont transmises chiffrées à Mindbody Payments et ne transitent jamais par nos serveurs en clair.</Text>
    </Stack>
  );
}
